import { create } from 'zustand';
import { persist } from 'zustand/middleware';

export interface CartItem {
  productId: string;
  name: string;
  productAttributes?: Record<string, string>;
  price: number;
  quantity: number;
  locationId?: string | null; // Fulfillment location (from inventory) - optional for org-level products
  locationName: string;
  image?: string;
  isPreorder?: boolean; // True if item was added as pre-order (out of stock)
  /**
   * Owning organization — used to fetch that org's tax rate.
   *
   * REQUIRED KEY, nullable value: writing `organizationId` at every addItem() call
   * site is enforced by the compiler on purpose. When it was optional, three of the
   * four call sites silently omitted it and the cart fell back to the hardcoded
   * default rate with no visible error. Pass `undefined` explicitly if a product
   * genuinely has no organization.
   */
  organizationId: string | undefined;
}

interface CartStore {
  items: CartItem[];
  discountCode: string | null;
  discountRate: number;
  addItem: (item: Omit<CartItem, 'quantity'> & { quantity?: number }) => void;
  removeItem: (productId: string) => void;
  updateQuantity: (productId: string, quantity: number) => void;
  clearCart: () => void;
  getTotal: () => number;
  getItemCount: () => number;
  getItemsByLocation: () => Map<string, CartItem[]>;
  hasPreorderItems: () => boolean;
  applyDiscountCode: (code: string) => boolean;
  clearDiscount: () => void;
}

export const useCartStore = create<CartStore>()(
  persist(
    (set, get) => ({
      items: [] as CartItem[],
      discountCode: null as string | null,
      discountRate: 0,

      addItem: (item: Omit<CartItem, 'quantity'> & { quantity?: number }) => {
        set((state: CartStore) => {
          const existingItem = state.items.find(
            (i: CartItem) => i.productId === item.productId
          );

          if (existingItem) {
            // Update quantity if item already exists. Backfill organizationId from the
            // incoming item: a cart persisted before that field existed rehydrates
            // without it, and this branch would otherwise discard the incoming value
            // forever, leaving the item permanently on the fallback tax rate.
            return {
              items: state.items.map((i: CartItem) =>
                i.productId === item.productId
                  ? {
                      ...i,
                      organizationId: i.organizationId ?? item.organizationId,
                      quantity: i.quantity + (item.quantity || 1),
                    }
                  : i
              ),
            };
          }

          // Add new item
          return {
            items: [...state.items, { ...item, quantity: item.quantity || 1 }],
          };
        });
      },

      removeItem: (productId: string) => {
        set((state: CartStore) => ({
          items: state.items.filter((i: CartItem) => i.productId !== productId),
        }));
      },

      updateQuantity: (productId: string, quantity: number) => {
        if (quantity <= 0) {
          get().removeItem(productId);
          return;
        }

        set((state: CartStore) => ({
          items: state.items.map((i: CartItem) =>
            i.productId === productId ? { ...i, quantity } : i
          ),
        }));
      },

      clearCart: () => {
        set({ items: [], discountCode: null, discountRate: 0 });
      },

      getTotal: () => {
        return get().items.reduce(
          (total: number, item: CartItem) => total + item.price * item.quantity,
          0
        );
      },

      getItemCount: () => {
        return get().items.reduce((count: number, item: CartItem) => count + item.quantity, 0);
      },

      getItemsByLocation: () => {
        const itemsByLocation = new Map<string, CartItem[]>();
        get().items.forEach((item: CartItem) => {
          // Group by locationId; items without locationId go into 'organization' group
          const locationId = item.locationId || 'organization';
          const locationItems = itemsByLocation.get(locationId) || [];
          locationItems.push(item);
          itemsByLocation.set(locationId, locationItems);
        });
        return itemsByLocation;
      },

      // Check if cart has any pre-order items
      hasPreorderItems: () => {
        return get().items.some((item: CartItem) => item.isPreorder);
      },

      applyDiscountCode: (code: string): boolean => {
        // 'new10' is the only recognized promo code, applying a 10% discount
        if (code === 'new10') {
          set({ discountCode: code, discountRate: 0.1 });
          return true;
        }
        return false;
      },

      clearDiscount: () => {
        set({ discountCode: null, discountRate: 0 });
      },
    }),
    {
      name: 'shopping-cart', // localStorage key
      version: 1,
      /**
       * v0 -> v1: `organizationId` became a required key on CartItem.
       *
       * This function is load-bearing purely by EXISTING: without a `migrate`, the
       * `version: 1` bump above makes zustand discard every cart persisted at v0.
       * It deliberately preserves those carts rather than dropping them.
       *
       * It does NOT recover the missing value — that is unknowable for a legacy item.
       * Such items fall back to the default rate for themselves only (tax is computed
       * per item, so they no longer drag the rest of the cart with them) and are
       * repaired when the product is next added, via addItem's backfill above.
       */
      migrate: (persistedState: unknown, version: number) => {
        const state = persistedState as { items?: Partial<CartItem>[] } | undefined;

        if (version === 0 && state?.items) {
          // Normalize the in-memory shape to the declared type. Note this is cosmetic
          // on its own: `undefined` does not survive JSON.stringify, so the persisted
          // record is unchanged. The preservation above is the point.
          return {
            ...state,
            items: state.items.map((item) => ({
              organizationId: undefined,
              ...item,
            })),
          };
        }

        return state;
      },
    }
  )
);
